import SearchIcon from '@mui/icons-material/Search';
import { AnalyticsExportButton } from './AnalyticsHeaderSlot';
import { Autocomplete, Box, Grid, IconButton, InputAdornment, TextField, Toolbar, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import moment from 'moment';
import React from 'react';

import RemoteAutocomplete from '../components/RemoteAutocomplete';
import { formatTourOptionLabel } from '../components/tourOptionLabel';
import StyledFilledTextField from '../components/StyledFilledTextField';
import { useIsWidthUp } from '../utils';
import { SENTIMENT_SCORE_OPTIONS } from '../settings/utils';

const useStyles = makeStyles((theme) => ({
    toolbarRoot: {
        margin: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            margin: 0,
        },
    },
    hidden: {
        visibility: 'hidden',
    },
    grow: {
        flexGrow: 1,
    },
    pillButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '86px',
        height: '32px',
        padding: '0px, 16px, 0px, 16px',
        borderRadius: '20px',
        gap: '4px',
        color: 'black',
        backgroundColor: '#D8D8D8',
        cursor: 'pointer',
    },
    selectedPillButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '86px',
        height: '32px',
        padding: '0px, 16px, 0px, 16px',
        borderRadius: '20px',
        gap: '4px',
        backgroundColor: '#007AFF',
        color: 'white',
        cursor: 'pointer',
    },
}));

// Travel-date presets: a pill sets `after` to N days ago, open-ended (null = all time).
const DATE_PRESET_DAYS = {
    'all time': null,
    'last 7': 7,
    'last 90': 90,
    'last year': 365,
};

// The full history is slow to load for large operators, so a fresh visit opens on the last week.
export const DEFAULT_DATE_PRESET = 'last 7';

export const datePresetAfter = (option) => {
    const days = DATE_PRESET_DAYS[option];
    if (!days) {
        return null;
    }
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString().split('T')[0];
};

export const SentimentAnalysisListToolbar = (props) => {
    const classes = useStyles();
    const { setRecordListFilters, recordListFilters } = props;
    const isSmallScreen = !useIsWidthUp('sm');
    const [remoteTripValue, setRemoteTripValue] = React.useState(null);
    const [remoteTagValue, setRemoteTagValue] = React.useState(null);

    const [inputValue, setInputValue] = React.useState(props.query ? props.query : '');
    // Starts at the same value as the input: starting empty made the first effect clear a query from the URL
    // (one list request) and the debounce put it back a second later (another).
    const [debouncedValue, setDebouncedValue] = React.useState(props.query ? props.query : '');

    React.useEffect(() => {
        props.setQuery(debouncedValue);
    }, [debouncedValue]);

    React.useEffect(() => {
        const isDeletion = inputValue.length < debouncedValue.length;

        const timerId = setTimeout(() => {
            setDebouncedValue(inputValue);
        }, isDeletion ? 0 : 1000); // If deleting, apply immediately; else wait for debounce

        return () => {
            clearTimeout(timerId);
        };
    }, [inputValue]);



    const search_value = (props.query && !inputValue) ? props.query : inputValue;

    const handleSentimentScoreFilterSelect = (ev, score_data) => {
        
        if (score_data.length === 0) {
            setRecordListFilters(prev => {
                const otherFilters = prev.filter(f => f.field !== 'sentiment_score');
                return [...otherFilters, { field: 'sentiment_score', value: null }];
            });

            return;
        }

        setRecordListFilters(prev => {
            const otherFilters = prev.filter(f => f.field !== 'sentiment_score');
            let removed_labels = [...new Set(score_data.map(item => item.id ? item.id : item))];
            return [...otherFilters, { field: 'sentiment_score', value: removed_labels, op: "isAnyOf" }];
        });
    };

    const handleTourFilterSelect = (ev, tour_data) => {
        let tour_ids = tour_data.map((t) => t.id ? t.id : t);
        if (tour_ids.length === 0) {
            tour_ids = null;
        }
        setRecordListFilters((prev) => {
            let tour_field = prev.filter((f) => f.field === 'tour_id')[0];
            return [...prev.filter((f) => f.field !== 'tour_id'), { ...tour_field, value: tour_ids, op: "isAnyOf" }];
        });
        setRemoteTripValue(tour_data);
    };

    const handleTagFilterSelect = (ev, tag_data) => {
        let tour_tags = tag_data.map((t) => t.name ? t.name : t);
        if (tour_tags.length === 0) {
            tour_tags = null;
        }
        setRecordListFilters((prev) => {
            let tour_field = prev.filter((f) => f.field === 'tour_tags')[0];
            return [...prev.filter((f) => f.field !== 'tour_tags'), { ...tour_field, value: tour_tags, op: "isAnyOf" }];
        });
        setRemoteTagValue(tag_data);
    };

    // [all, 7, 90, 365] days tab filters handling. A preset replaces the whole travel-date range: an end date left
    // over from the date picker would otherwise narrow it. The op is explicit; without one the backend used to
    // fall back to a text match (icontains).
    const handleDateFilterSelect = (option) => {
        const date_filter = datePresetAfter(option);
        setRecordListFilters((prev) => {
            let after_field = prev.filter((f) => f.field === 'after')[0];
            let before_field = prev.filter((f) => f.field === 'before')[0];
            return [
                ...prev.filter((f) => f.field !== 'after' && f.field !== 'before'),
                { ...after_field, field: 'after', value: date_filter, op: '>' },
                { ...before_field, field: 'before', value: null },
            ];
        });
    };

    const isThisDateFilterSelected = (option) => {
        const after = recordListFilters.filter((f) => f.field === 'after')[0]?.value || null;
        const before = recordListFilters.filter((f) => f.field === 'before')[0]?.value || null;
        return !before && after === datePresetAfter(option);
    };



    const handleDateRangeChange = (dateRange) => {
        let dateStart = dateRange[0];
        let dateEnd = dateRange[1];
        if (dateStart) {
            dateStart = moment.utc(dateStart);
            dateStart = dateStart.toISOString().split('T')[0];
        }
        if (dateEnd) {
            dateEnd = moment.utc(dateEnd);
            dateEnd = dateEnd.toISOString().split('T')[0];
        }


        setRecordListFilters((prev) => {
            let dateStartField = prev.filter((f) => f.field === 'after')[0];
            let dateEndField = prev.filter((f) => f.field === 'before')[0];
            return [
                ...prev.filter((f) => f.field !== 'after' && f.field !== 'before'),
                { ...dateStartField, value: dateStart, op: '>' },
                { ...dateEndField, value: dateEnd, op: '<'},
            ];
        });
    }


    const IsoToMomentFormat = (isoDate) => {
        if (!isoDate) {
            return null;
        }
        return moment.utc(isoDate);
    }

    const handleExportCSV = async () => {
        await props.onExportList();
    }


   return (
        <Toolbar disableGutters={true} className={classes.toolbarRoot}>
            <Grid container justifyContent="space-between" alignItems="center">
                <Grid item xs={12} sm={9} container spacing={2} alignItems={'center'}>
                    <Grid item xs={12} sm={3}>
                        <StyledFilledTextField
                            label="Search"
                            size="small"
                            onChange={(ev) => setInputValue(ev.target.value)}
                            value={search_value}
                            fullWidth
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton aria-label="do search">
                                            <SearchIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <Autocomplete
                            multiple
                            value={recordListFilters.filter(rlf => rlf.field === 'sentiment_score')[0]?.value || []}
                            options={SENTIMENT_SCORE_OPTIONS.filter(option => !recordListFilters.filter(rlf => rlf.field === 'sentiment_score')[0]?.value?.includes(option?.value))}
                            getOptionLabel={(option) => option.value ? option.value : option}
                            onChange={handleSentimentScoreFilterSelect}
                            isOptionEqualToValue={(option, value) => String(option.id) === value}
                            renderInput={(params) => <TextField {...params} variant="filled" label="Sentiment Score" size="small" />}
                        />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DateRangePicker
                                slots={{ field: SingleInputDateRangeField }}
                                name="allowedRange"
                                variant="filled"
                                onChange={handleDateRangeChange}
                                format="MMMM D, YYYY"
                                todayLabel="today"
                                showTodayButton
                                timezone='utc'
                                autoOk
                                label={'Travel Date'}
                                value={[IsoToMomentFormat(recordListFilters.filter(rlf => rlf.field === 'after')[0]?.value), IsoToMomentFormat(recordListFilters.filter(rlf => rlf.field === 'before')[0]?.value)]}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        variant: 'filled',
                                        fullWidth: true
                                    },
                                    actionBar: {
                                        actions: ['clear', 'accept'],
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <RemoteAutocomplete
                            filterSelected
                            InputProps={{ required: true }}
                            resource="tour"
                            label="Tour"
                            multiple
                            variant="filled"
                            size="small"
                            apiParams={{ fields: 'id,name,is_bespoke,start_date,end_date' }}
                            onChange={handleTourFilterSelect}
                            value={remoteTripValue ? remoteTripValue : recordListFilters.filter(rlf => rlf.field === 'tour_id')[0]?.value ? recordListFilters.filter(rlf => rlf.field === 'tour_id')[0]?.value : []}
                            getOptionLabel={(opt) => formatTourOptionLabel(opt)}
                        />
                    </Grid>
                   <Grid item xs={12} sm={2}>
                       <RemoteAutocomplete
                           filterSelected
                           InputProps={{ required: true }}
                           resource="tour_tag"
                           label="Tags"
                           multiple
                           variant="filled"
                           size="small"
                           onChange={handleTagFilterSelect}
                           value={remoteTagValue ? remoteTagValue : recordListFilters.filter(rlf => rlf.field === 'tour_tags')[0]?.value ? recordListFilters.filter(rlf => rlf.field === 'tour_tags')[0]?.value : []}
                           getOptionLabel={(opt) => {
                               if (!opt || !opt.name) {
                                   return "Tag ID:" + opt
                               }
                               return opt.category.name + ": " + opt.name;
                           }}
                       />
                   </Grid>
                </Grid>
                <Grid item xs={12} sm={3} container pt={isSmallScreen ? 2 : 0} pb={0} justifyContent={isSmallScreen ? 'space-between' : "flex-end"} gap={1}>
                    <Box className={isThisDateFilterSelected('all time') ? classes.selectedPillButton : classes.pillButton} onClick={() => handleDateFilterSelect('all time')} style={{}}>
                        <Typography fontSize={16}>All time</Typography>
                    </Box>
                    <Box className={isThisDateFilterSelected('last 7') ? classes.selectedPillButton : classes.pillButton} onClick={() => handleDateFilterSelect('last 7')} style={{ width: '113px' }}>
                        <Typography fontSize={16}>Last 7 days</Typography>
                    </Box>
                    <Box className={isThisDateFilterSelected('last 90') ? classes.selectedPillButton : classes.pillButton} onClick={() => handleDateFilterSelect('last 90')} style={{ width: '123px' }}>
                        <Typography fontSize={16}>Last 90 days</Typography>
                    </Box>
                    <Box className={isThisDateFilterSelected('last year') ? classes.selectedPillButton : classes.pillButton} onClick={() => handleDateFilterSelect('last year')} style={{ width: '93px' }}>
                        <Typography fontSize={16}>Last year</Typography>
                    </Box>
                    <AnalyticsExportButton
                        isSmallScreen={isSmallScreen}
                        onClick={handleExportCSV}
                        tooltip="Export list as CSV"
                        anchorStyle={isSmallScreen ? {
                            position: 'absolute',
                            right: 0,
                            top: -99,
                            zIndex: 1000,
                        } : {
                            position: 'fixed',
                            right: 10,
                            top: 10,
                            zIndex: 1000,
                        }}
                    />
                </Grid>
            </Grid>
        </Toolbar>
    );
};

export default SentimentAnalysisListToolbar;
